import React, { createContext, useContext, useMemo } from 'react';
import useLocalStorage from 'use-local-storage';

/**
 * Feature flags applicatifs (front) persistés en localStorage. Permet de merger une refonte en cours
 * (composants partagés) tout en gardant la nouvelle UI derrière un interrupteur activable par un
 * testeur/dev depuis les Paramètres — sans configuration serveur. Client-side uniquement : ce n'est
 * PAS un contrôle d'accès (n'importe qui peut l'activer), c'est un opt-in interne.
 */
export type FeatureFlags = {
  /** Active la nouvelle fiche mission (Mission Dashboard V2). Activé par défaut → V2. */
  missionDashboardV2: boolean;
  /** Active la nouvelle liste « Gestion des jobs » (V2). Activé par défaut → V2. */
  jobsDashboardV2: boolean;
};

export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  missionDashboardV2: true,
  jobsDashboardV2: true,
};

const STORAGE_KEY = 'bloomays.featureFlags';

type FeatureFlagsContextValue = {
  flags: FeatureFlags;
  setFlag: <K extends keyof FeatureFlags>(key: K, value: FeatureFlags[K]) => void;
};

// Valeur par défaut = flags par défaut + setter no-op, pour rester robuste si un composant est rendu
// hors provider (stories, tests isolés) plutôt que de crasher. En prod, le provider est monté dans app.tsx.
const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: DEFAULT_FEATURE_FLAGS,
  setFlag: () => undefined,
});

export const FeatureFlagsProvider = ({ children }: { children: React.ReactNode }) => {
  const [stored, setStored] = useLocalStorage<FeatureFlags>(STORAGE_KEY, DEFAULT_FEATURE_FLAGS);
  // Fusion avec les défauts : robuste si on ajoute des flags plus tard (anciennes valeurs stockées).
  const flags = useMemo<FeatureFlags>(() => ({ ...DEFAULT_FEATURE_FLAGS, ...stored }), [stored]);

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({
      flags,
      setFlag: (key, val) => setStored({ ...flags, [key]: val }),
    }),
    [flags, setStored],
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

export const useFeatureFlags = (): FeatureFlagsContextValue => useContext(FeatureFlagsContext);

/** Lecture d'un flag unique (réactif : re-render quand le flag change dans les Paramètres). */
export const useFeatureFlag = <K extends keyof FeatureFlags>(key: K): FeatureFlags[K] => useFeatureFlags().flags[key];
